import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Button, Chip, Collapse, CircularProgress, Divider,
  IconButton, LinearProgress, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CampaignRecommendationCard from './CampaignRecommendationCard';

// ── Theme ──────────────────────────────────────────────────────────────────
const GREEN     = '#2db838';
const GREEN_DRK = '#1e9128';

// ── Helpers ────────────────────────────────────────────────────────────────
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Separate time pools so urgent segments get morning slots, others get spread
const MORNING_TIMES = ['09:00', '09:30', '10:00', '10:30'];
const AFTERNOON_TIMES = ['13:00', '14:00', '15:00', '16:00', '11:00'];
const ALL_TIMES = [...MORNING_TIMES, ...AFTERNOON_TIMES];

function priorityBadge(confidence) {
  if (confidence >= 90) return { label: 'Urgent',      color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5' };
  if (confidence >= 80) return { label: 'High Intent', color: GREEN,     bg: '#e6f9e8', border: `${GREEN}55` };
  if (confidence >= 65) return { label: 'Ready',       color: '#b45309', bg: '#fef9c3', border: '#fde68a' };
  return                       { label: 'Standard',    color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' };
}

function weekRange(isoDate) {
  const d = new Date(isoDate);
  const end = new Date(d); end.setDate(d.getDate() + 6);
  const s = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const e = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return { start: d, end, label: `${s} — ${e}` };
}

function weekTitle(isoDate) {
  const d = new Date(isoDate);
  const end = new Date(d); end.setDate(d.getDate() + 6);
  const day1 = d.toLocaleDateString('en-GB', { day: 'numeric' });
  const day2 = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${day1} — ${day2}`;
}

function timeAgo(isoString) {
  if (!isoString) return null;
  const secs = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (secs < 60)   return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function timeToMins(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const TIMES_SORTED = [...ALL_TIMES].sort((a, b) => timeToMins(a) - timeToMins(b));
const MORNING_SORTED = [...MORNING_TIMES].sort((a, b) => timeToMins(a) - timeToMins(b));

const MAX_CONTACTS_PER_DAY = 1000;
const MAX_SEGMENTS_PER_DAY = 3;
const WEEKDAY_INDICES = [0, 1, 2, 3, 4]; // Mon–Fri

function assignToDays(suggestions, weekStart) {
  const map  = {};
  const now  = new Date();
  const weekStartDate = new Date(weekStart);
  const isFutureWeek  = weekStartDate > now;

  // ── Schedulable day range ────────────────────────────────────────────────
  let todayIdx, futureTimes, firstSchedulable;

  if (isFutureWeek) {
    // Any day of a future week is schedulable; all time slots are open
    todayIdx       = 0;
    futureTimes    = TIMES_SORTED;
    firstSchedulable = 0;
  } else {
    todayIdx = Math.min(6, Math.max(0, Math.floor(
      (now.getTime() - weekStartDate.getTime()) / 86400000
    )));
    const nowMins = now.getHours() * 60 + now.getMinutes();
    futureTimes = TIMES_SORTED.filter(t => timeToMins(t) > nowMins + 30);
    firstSchedulable = (futureTimes.length === 0) ? todayIdx + 1 : todayIdx;
  }

  const weekdays    = WEEKDAY_INDICES.filter(d => d >= firstSchedulable);
  const allRemaining = Array.from({ length: 7 - firstSchedulable }, (_, i) => firstSchedulable + i);
  const schedulableDays = weekdays.length > 0 ? weekdays : allRemaining;

  // ── Per-day counters ─────────────────────────────────────────────────────
  const contactsPerDay = {};
  const segmentsPerDay = {};

  // ── Helpers ──────────────────────────────────────────────────────────────
  function canFit(dayIdx, size) {
    const segs = segmentsPerDay[dayIdx] ?? 0;
    const used = contactsPerDay[dayIdx] ?? 0;
    if (segs >= MAX_SEGMENTS_PER_DAY) return false;
    // An empty day always accepts the first segment even if it alone exceeds
    // the contact cap — we cannot split further at this layer
    return used === 0 || used + size <= MAX_CONTACTS_PER_DAY;
  }

  function pickDay(size, preferredPool) {
    // First: find a preferred day that fits
    for (const d of preferredPool) {
      if (canFit(d, size)) return d;
    }
    // Second: any schedulable day that fits
    for (const d of schedulableDays) {
      if (canFit(d, size)) return d;
    }
    // Overflow: all days at capacity — pick the least-loaded by contact count
    return schedulableDays.reduce((best, d) =>
      (contactsPerDay[d] ?? 0) < (contactsPerDay[best] ?? 0) ? d : best
    , schedulableDays[schedulableDays.length - 1]);
  }

  // Per-day time slot counters, split by priority bucket so urgent segments
  // consume morning slots independently from standard segments.
  const urgentSlotCounters   = {};
  const standardSlotCounters = {};

  function nextTimeSlot(dayIdx, conf) {
    const isUrgent = conf >= 80;
    if (dayIdx === todayIdx && futureTimes.length > 0) {
      // Today: only future time slots; pick from morning-first if urgent
      const pool = isUrgent
        ? futureTimes.filter(t => MORNING_SORTED.includes(t)).concat(futureTimes)
        : futureTimes;
      const counters = isUrgent ? urgentSlotCounters : standardSlotCounters;
      const idx = counters[dayIdx] ?? 0;
      counters[dayIdx] = idx + 1;
      return pool[idx % pool.length];
    }
    if (isUrgent) {
      const idx = urgentSlotCounters[dayIdx] ?? 0;
      urgentSlotCounters[dayIdx] = idx + 1;
      return MORNING_SORTED[idx % MORNING_SORTED.length];
    }
    const idx = standardSlotCounters[dayIdx] ?? 0;
    standardSlotCounters[dayIdx] = idx + 1;
    return TIMES_SORTED[idx % TIMES_SORTED.length];
  }

  function place(s, dayIdx, consumeCapacity = true) {
    if (consumeCapacity) {
      contactsPerDay[dayIdx] = (contactsPerDay[dayIdx] ?? 0) + (s.estimated_size ?? 0);
      segmentsPerDay[dayIdx] = (segmentsPerDay[dayIdx] ?? 0) + 1;
    }
    if (!map[dayIdx]) map[dayIdx] = [];
    map[dayIdx].push({ ...s, _assignedTime: nextTimeSlot(dayIdx, s.confidence_score ?? 0) });
  }

  // ── Day pools by confidence tier ─────────────────────────────────────────
  const urgentPool   = schedulableDays.slice(0, Math.min(2, schedulableDays.length));
  const highPool     = schedulableDays.slice(0, Math.max(1, Math.ceil(schedulableDays.length * 0.6)));
  const readyPool    = schedulableDays.slice(0, Math.max(1, Math.ceil(schedulableDays.length * 0.8)));
  const standardPool = schedulableDays;

  // ── Separate actionable from inactive ────────────────────────────────────
  const isActionable = s => s.status !== 'rejected' && s.recommendation?.status !== 'converted';

  const actionable = suggestions
    .filter(isActionable)
    .sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0));

  const inactive = suggestions.filter(s => !isActionable(s));

  // ── Assign actionable segments ────────────────────────────────────────────
  actionable.forEach(s => {
    const conf            = s.confidence_score ?? 0;
    const size            = s.estimated_size ?? 0;
    const suggestedOffset = s.segment_filters_json?.suggested_day_offset;

    // If backend supplied a day offset for this chunk, try to honour it.
    // This spreads multi-chunk segments (Part 1/3, 2/3, 3/3) across Mon/Wed/Fri
    // instead of stacking them on the same day.
    if (suggestedOffset !== null && suggestedOffset !== undefined) {
      const targetDay = suggestedOffset; // absolute: 0=Mon … 6=Sun
      if (schedulableDays.includes(targetDay) && canFit(targetDay, size)) {
        place(s, targetDay);
        return;
      }
    }

    // Fall back to confidence-pool placement
    const pool = conf >= 90 ? urgentPool : conf >= 80 ? highPool : conf >= 65 ? readyPool : standardPool;
    place(s, pickDay(size, pool));
  });

  // ── Place inactive on their original day (no capacity cost) ──────────────
  inactive.forEach(s => {
    const sendDate = s.recommendation?.suggested_send_date;
    let dayIdx = Math.max(0, todayIdx);
    if (sendDate) {
      const diff = Math.floor((new Date(sendDate) - new Date(weekStart)) / 86400000);
      dayIdx = Math.max(0, Math.min(6, diff));
    }
    place(s, dayIdx, false);
  });

  return map;
}

// ── PulsingDot ─────────────────────────────────────────────────────────────
function PulsingDot({ color = GREEN, size = 8 }) {
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, mt: '1px' }}>
      <motion.div
        animate={{ scale: [1, 2, 1], opacity: [0.6, 0, 0.6] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%', backgroundColor: color,
        }}
      />
      <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, position: 'relative' }} />
    </Box>
  );
}

// ── SegmentCard ────────────────────────────────────────────────────────────
function SegmentCard({
  suggestion, time, onApproveAndPreview, onReject,
  onRecommendationApprove, onRecommendationReject, onConvert,
  onWhatsAppBroadcast, onEmailBroadcast, onViewReport,
}) {
  const [loading, setLoading] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const handle = async (fn) => { if (typeof fn !== 'function') return; setLoading(true); try { await fn(); } finally { setLoading(false); } };

  const { audience_name, description, status, estimated_size,
          confidence_score, rationale, segment_filters_json, recommendation } = suggestion;

  const isSent        = recommendation?.status === 'converted';
  const isApproved    = status === 'approved';
  const isPending     = status === 'pending_review';
  const isRejected    = status === 'rejected';
  const isEmailDriven = segment_filters_json?.type === 'email_analysis';
  const isUrgent      = (confidence_score ?? 0) >= 90;
  const chunkNum      = segment_filters_json?.chunk_num;
  const totalChunks   = segment_filters_json?.total_chunks;
  const priority      = priorityBadge(confidence_score);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: isSent ? '#e5e7eb'
            : isPending && isUrgent ? '#fca5a5'
            : isPending ? '#e5e7eb'
            : isApproved ? `${GREEN}44`
            : '#fecaca',
          borderStyle: isSent ? 'dashed' : 'solid',
          borderRadius: 2,
          mb: 1.5,
          overflow: 'hidden',
          bgcolor: isSent ? '#fafafa' : isPending && isUrgent ? '#fff8f8' : '#fff',
          opacity: isRejected ? 0.55 : 1,
        }}
      >
        {/* Urgent banner */}
        {isUrgent && isPending && (
          <Stack direction="row" alignItems="center" spacing={0.75}
            sx={{ px: 2, py: 0.5, bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', letterSpacing: 0.3 }}>
              URGENT — send this week for best conversion
            </Typography>
          </Stack>
        )}

        {/* Email analysis banner */}
        {isEmailDriven && !isUrgent && (
          <Stack direction="row" alignItems="center" spacing={0.75}
            sx={{ px: 2, py: 0.6, bgcolor: '#eff6ff', borderBottom: '1px solid #dbeafe' }}>
            <EmailIcon sx={{ fontSize: 12, color: '#3b82f6' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', letterSpacing: 0.3 }}>
              FROM EMAIL ANALYSIS — based on your inbox
            </Typography>
          </Stack>
        )}

        <Box sx={{ p: 2 }}>
          {/* Top row: time + name + badge + action button */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5} mb={0.75}>
            {/* Left: time + name + badges */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Stack direction="row" alignItems="center" spacing={0.4}>
                  <AccessTimeIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                  <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{time}</Typography>
                </Stack>

                <Typography variant="subtitle2" fontWeight={700} sx={{ color: isSent ? '#9ca3af' : '#111827' }}>
                  {audience_name}
                </Typography>

                {/* Priority badge */}
                <Box sx={{
                  px: 0.9, py: 0.15,
                  bgcolor: isSent ? '#f3f4f6' : priority.bg,
                  border: `1px solid ${isSent ? '#e5e7eb' : priority.border}`,
                  borderRadius: 0.75,
                }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 700,
                    color: isSent ? '#9ca3af' : priority.color,
                    letterSpacing: 0.4,
                  }}>
                    {priority.label.toUpperCase()}
                  </Typography>
                </Box>

                {isRejected && (
                  <Typography sx={{ fontSize: 10, color: '#ef4444', fontWeight: 700 }}>REJECTED</Typography>
                )}
              </Stack>
            </Box>

            {/* Right: action */}
            {isSent && (
              <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: GREEN }} />
                  <Typography sx={{ fontSize: 13, color: GREEN, fontWeight: 700 }}>Sent</Typography>
                </Stack>
                {recommendation?.email_campaign_id ? (
                  <Button
                    size="small"
                    onClick={() => onViewReport?.(recommendation.email_campaign_id)}
                    sx={{ fontSize: 11, color: '#6b7280', textTransform: 'none', p: 0, minWidth: 0,
                          '&:hover': { color: GREEN, background: 'none' } }}
                  >
                    View report →
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>View report</Typography>
                )}
              </Stack>
            )}

            {isPending && (
              <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={loading}
                  onClick={() => handle(onApproveAndPreview)}
                  endIcon={loading ? <CircularProgress size={12} color="inherit" /> : null}
                  sx={{
                    bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DRK },
                    fontWeight: 700, borderRadius: 5, px: 2, py: 0.5,
                    textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap',
                  }}
                >
                  {loading ? 'Generating AI campaign…' : 'Approve & launch →'}
                </Button>
                <Button
                  size="small"
                  onClick={() => handle(onReject)}
                  disabled={loading}
                  sx={{ color: '#9ca3af', fontSize: 11, textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  Reject
                </Button>
              </Stack>
            )}

            {isApproved && !isSent && (
              <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Stack direction="row" spacing={0.75}>
                  <Tooltip title="Send mass email">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EmailIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => onEmailBroadcast?.(suggestion)}
                      sx={{
                        bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' },
                        fontWeight: 700, borderRadius: 5, px: 1.5,
                        textTransform: 'none', fontSize: 11,
                      }}
                    >
                      Send Email
                    </Button>
                  </Tooltip>
                </Stack>
                {recommendation && (
                  <Typography
                    onClick={() => setRecOpen((p) => !p)}
                    sx={{ fontSize: 11, color: '#6b7280', cursor: 'pointer', '&:hover': { color: GREEN } }}
                  >
                    {recOpen ? 'Hide' : 'View'} recommendation
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>

          {/* Description */}
          <Typography variant="body2" sx={{ color: isSent ? '#9ca3af' : '#6b7280', mb: 1, lineHeight: 1.55 }}>
            {description}
          </Typography>

          {/* Stats row */}
          <Stack direction="row" spacing={0} divider={
            <Box sx={{ mx: 1.5, color: '#d1d5db', userSelect: 'none' }}>|</Box>
          } alignItems="center" flexWrap="wrap">
            <Typography sx={{ fontSize: 12, color: isSent ? '#9ca3af' : '#374151', fontWeight: 500 }}>
              {estimated_size?.toLocaleString()} contacts
            </Typography>
            {chunkNum && totalChunks && (
              <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
                batch {chunkNum}/{totalChunks}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: isSent ? '#9ca3af' : GREEN_DRK, fontWeight: 600 }}>
              {confidence_score}% confidence
            </Typography>
            {rationale && (
              <Typography sx={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }} noWrap>
                {rationale.length > 60 ? rationale.slice(0, 57) + '…' : rationale}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Campaign recommendation drawer (approved, not yet sent) */}
        {isApproved && !isSent && recOpen && recommendation && (
          <Box sx={{ borderTop: '1px solid #f0fdf4', bgcolor: '#f9fbe7' }}>
            <CampaignRecommendationCard
              recommendation={recommendation}
              onApprove={onRecommendationApprove}
              onReject={onRecommendationReject}
              onConvert={onConvert}
            />
          </Box>
        )}

        {isApproved && !isSent && !recommendation && (
          <Box sx={{ borderTop: '1px solid #e5e7eb', px: 2, py: 1.25, bgcolor: '#fafafa' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={13} thickness={5} sx={{ color: GREEN }} />
              <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>Generating AI campaign recommendation…</Typography>
            </Stack>
          </Box>
        )}
      </Paper>
    </motion.div>
  );
}

// ── WeekCalendar — day-by-day view for one week ────────────────────────────
function WeekCalendar({
  week, onApproveAndPreview, onReject,
  onRecommendationApprove, onRecommendationReject, onConvert,
  onWhatsAppBroadcast, onEmailBroadcast, onViewReport,
}) {
  const suggestions = week.suggestions ?? [];
  const dayMap = useMemo(() => assignToDays(suggestions, week.week_of), [suggestions, week.week_of]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(week.week_of); d.setDate(d.getDate() + i); return d;
  }), [week.week_of]);

  const activeDayIndices = Object.keys(dayMap).map(Number).sort((a, b) => a - b);

  if (activeDayIndices.length === 0) {
    return (
      <Box sx={{ py: 2, px: 1, color: '#9ca3af', fontSize: 13 }}>
        No segments scheduled for this week.
      </Box>
    );
  }

  return (
    <AnimatePresence>
      {activeDayIndices.map((dayIdx) => {
        const daySuggestions = dayMap[dayIdx] || [];
        const date = weekDates[dayIdx];
        const dayName    = date ? DAYS[dayIdx] : `Day ${dayIdx + 1}`;
        const dayNum     = date ? date.getDate() : '';
        const daySent    = daySuggestions.filter((s) => s.recommendation?.status === 'converted').length;
        const dayPending = daySuggestions.filter((s) => s.status === 'pending_review').length;

        return (
          <motion.div
            key={dayIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: dayIdx * 0.04 }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 2.5, mb: 1 }}>
              <Box sx={{ pt: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', mb: 0.25 }}>
                  {dayName}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1, mb: 0.4 }}>
                  {dayNum}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                  {daySent > 0 && `${daySent} sent`}
                  {daySent > 0 && dayPending > 0 && ' · '}
                  {dayPending > 0 && `${dayPending} pending`}
                </Typography>
              </Box>
              <Box>
                {daySuggestions.map((s) => (
                  <SegmentCard
                    key={s.id}
                    suggestion={s}
                    time={s._assignedTime}
                    onApproveAndPreview={() => onApproveAndPreview(s.id)}
                    onReject={() => onReject(s.id)}
                    onRecommendationApprove={() => onRecommendationApprove(s.id, s.recommendation?.id)}
                    onRecommendationReject={() => onRecommendationReject(s.id, s.recommendation?.id)}
                    onConvert={(mode) => onConvert(s.id, s.recommendation?.id, mode)}
                    onWhatsAppBroadcast={onWhatsAppBroadcast}
                    onEmailBroadcast={onEmailBroadcast}
                    onViewReport={onViewReport}
                  />
                ))}
              </Box>
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#f3f4f6' }} />
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

// ── WeeklyPlanView (main export) ───────────────────────────────────────────
export default function WeeklyPlanView({
  weeklyPlan,
  planLoading,
  discovering,
  clearing,
  onRunDiscovery,
  onClearPlan,
  onApproveAndPreview,
  onReject,
  onRecommendationApprove,
  onRecommendationReject,
  onConvert,
  onWhatsAppBroadcast,
  onEmailBroadcast,
  onViewReport,
}) {
  const weeks = weeklyPlan?.weeks ?? [];

  // Aggregate totals across all 4 weeks
  const allSuggestions = useMemo(() => weeks.flatMap((w) => w.suggestions ?? []), [weeks]);
  const total    = allSuggestions.length;
  const approved = allSuggestions.filter((s) => s.status === 'approved').length;
  const pending  = allSuggestions.filter((s) => s.status === 'pending_review').length;
  const rejected = allSuggestions.filter((s) => s.status === 'rejected').length;
  const sent     = allSuggestions.filter((s) => s.recommendation?.status === 'converted').length;

  const monthStart = weeklyPlan?.month_start;

  // This week open by default; future weeks collapsed
  const [expandedWeeks, setExpandedWeeks] = useState(new Set([0]));
  const toggleWeek = (i) => setExpandedWeeks((prev) => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  // Month label: "May — Jun 2026"
  const monthLabel = useMemo(() => {
    if (!monthStart) return 'This month';
    const start = new Date(monthStart);
    const end   = new Date(monthStart);
    end.setDate(end.getDate() + 27); // ~4 weeks
    const s = start.toLocaleDateString('en-GB', { month: 'long' });
    const e = end.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    return s === e.split(' ')[0] ? `${s} ${start.getFullYear()}` : `${s} — ${e}`;
  }, [monthStart]);

  const sentPct     = total ? (sent                / total) * 100 : 0;
  const approvedPct = total ? ((approved - sent)   / total) * 100 : 0;
  const pendingPct  = total ? (pending             / total) * 100 : 0;

  const sharedCardProps = {
    onApproveAndPreview,
    onReject,
    onRecommendationApprove,
    onRecommendationReject,
    onConvert,
    onWhatsAppBroadcast,
    onEmailBroadcast,
    onViewReport,
  };

  return (
    <Box sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
              <PulsingDot color={GREEN} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: GREEN, letterSpacing: 1, textTransform: 'uppercase' }}>
                Monthly Plan
              </Typography>
            </Stack>

            <Typography variant="h4" fontWeight={800} sx={{ color: '#111827', lineHeight: 1.15, mb: 0.5 }}>
              {monthLabel}
            </Typography>

            <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
              {total > 0
                ? `${total} segment${total !== 1 ? 's' : ''} across 4 weeks · ${approved} approved, ${pending} pending`
                : 'No segments yet — run discovery to generate a monthly plan'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" pt={0.5}>
            {total > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onClearPlan?.()}
                disabled={clearing || discovering}
                sx={{
                  borderColor: '#fca5a5', color: '#ef4444',
                  fontWeight: 600, textTransform: 'none', borderRadius: 2,
                  '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' },
                }}
              >
                {clearing ? 'Clearing…' : 'Clear plan'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={discovering ? <CircularProgress size={13} /> : <AutorenewIcon />}
              onClick={() => onRunDiscovery?.()}
              disabled={discovering || clearing}
              sx={{
                borderColor: '#d1d5db', color: '#374151',
                fontWeight: 600, textTransform: 'none', borderRadius: 2,
                '&:hover': { borderColor: GREEN, color: GREEN, bgcolor: '#f0fdf4' },
              }}
            >
              {discovering ? 'Discovering…' : 'Run discovery'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── Loading ── */}
      {planLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      )}

      {/* ── Month at a glance ────────────────────────────────────────────── */}
      {!planLoading && total > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              Month at a glance
            </Typography>
            <Stack direction="row" spacing={3}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>{total}</Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>segments</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>
                  {allSuggestions.reduce((s, sg) => s + (sg.estimated_size || 0), 0).toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>contacts total</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>{approved}</Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>approved</Typography>
              </Box>
            </Stack>
          </Stack>

          <Stack direction="row" sx={{ height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#f3f4f6' }}>
            {sentPct > 0 && <Box sx={{ width: `${sentPct}%`, bgcolor: GREEN_DRK, transition: 'width 0.6s' }} />}
            {approvedPct > 0 && <Box sx={{ width: `${approvedPct}%`, bgcolor: `${GREEN}88`, transition: 'width 0.6s' }} />}
            {pendingPct > 0 && <Box sx={{ width: `${pendingPct}%`, bgcolor: '#d1fae5', transition: 'width 0.6s' }} />}
          </Stack>

          <Stack direction="row" spacing={2} mt={1}>
            {[
              { label: 'Sent',     n: sent,           color: GREEN_DRK, dot: true },
              { label: 'Approved', n: approved - sent, color: GREEN,    dot: true },
              { label: 'Pending',  n: pending,         color: '#9ca3af', dot: false },
              { label: 'Rejected', n: rejected,        color: '#ef4444', dot: false },
            ].filter(({ n }) => n > 0).map(({ label, n, color, dot }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot ? color : 'transparent', border: dot ? 'none' : `1.5px solid ${color}` }} />
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  <span style={{ color, fontWeight: 700 }}>{n}</span> {label}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Per-week mini summary */}
          {weeks.length > 0 && (
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
              {weeks.map((w, i) => {
                const wSegs    = (w.suggestions ?? []).length;
                const wPending = (w.suggestions ?? []).filter((s) => s.status === 'pending_review').length;
                return (
                  <Chip
                    key={w.week_of}
                    label={`${w.label}: ${wSegs} seg${wSegs !== 1 ? 's' : ''}${wPending > 0 ? ` · ${wPending} pending` : ''}`}
                    size="small"
                    onClick={() => { toggleWeek(i); setExpandedWeeks((prev) => { const n = new Set(prev); n.add(i); return n; }); }}
                    sx={{
                      fontSize: 11,
                      bgcolor: i === 0 ? '#d1fae5' : '#f3f4f6',
                      color: i === 0 ? GREEN_DRK : '#374151',
                      fontWeight: i === 0 ? 700 : 400,
                      cursor: 'pointer',
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* ── Empty state ── */}
      {!planLoading && total === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          <Paper elevation={0} sx={{
            p: 5, textAlign: 'center', borderRadius: 2,
            border: '2px dashed #d1fae5', bgcolor: '#f0fdf4',
          }}>
            <Typography sx={{ fontSize: 32, mb: 1 }}>📭</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#111827', mb: 0.75 }}>
              No monthly plan yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 3, maxWidth: 380, mx: 'auto' }}>
              Run discovery to analyse your contacts and build a 4-week campaign plan.
              High-priority audiences land in week 1; lower-priority segments are spread
              across the month or deferred.
            </Typography>
            <Button
              variant="contained"
              startIcon={discovering ? <CircularProgress size={15} color="inherit" /> : <AutorenewIcon />}
              onClick={() => onRunDiscovery?.()}
              disabled={discovering}
              sx={{
                bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DRK },
                fontWeight: 700, borderRadius: 2, textTransform: 'none',
              }}
            >
              {discovering ? 'Discovering…' : 'Run Discovery Now'}
            </Button>
          </Paper>
        </motion.div>
      )}

      {/* ── 4-week sections ──────────────────────────────────────────────── */}
      {!planLoading && total > 0 && weeks.map((week, weekIdx) => {
        const wSugs    = week.suggestions ?? [];
        const wPending = wSugs.filter((s) => s.status === 'pending_review').length;
        const wSent    = wSugs.filter((s) => s.recommendation?.status === 'converted').length;
        const isOpen   = expandedWeeks.has(weekIdx);

        return (
          <Box key={week.week_of} sx={{ mb: 2 }}>
            {/* Week header ─────────────────────────────────────────────── */}
            <Stack
              direction="row" alignItems="center" justifyContent="space-between"
              onClick={() => toggleWeek(weekIdx)}
              sx={{
                cursor: 'pointer', px: 2, py: 1.5, borderRadius: 2,
                border: '1px solid #e5e7eb',
                bgcolor: isOpen ? '#fafafa' : '#fff',
                '&:hover': { bgcolor: '#f9fafb', borderColor: `${GREEN}55` },
                transition: 'background 0.15s',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>
                  {week.label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
                  {weekRange(week.week_of).label}
                </Typography>
                {weekIdx === 0 && (
                  <Chip label="Active" size="small" sx={{ bgcolor: '#d1fae5', color: GREEN_DRK, fontWeight: 700, fontSize: 10, height: 18 }} />
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                {wPending > 0 && (
                  <Typography sx={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
                    {wPending} pending
                  </Typography>
                )}
                {wSent > 0 && (
                  <Typography sx={{ fontSize: 12, color: GREEN_DRK, fontWeight: 600 }}>
                    {wSent} sent
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
                  {wSugs.length} segment{wSugs.length !== 1 ? 's' : ''}
                </Typography>
                {isOpen ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
              </Stack>
            </Stack>

            {/* Week body ───────────────────────────────────────────────── */}
            <Collapse in={isOpen} timeout={200}>
              <Box sx={{ pt: 2, pb: 0.5, pl: 0.5 }}>
                <WeekCalendar week={week} {...sharedCardProps} />
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {/* ── Footer ── */}
      {!planLoading && total > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mt={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PulsingDot color={GREEN} size={7} />
            <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
              Live AI analysis · Runs every Sunday at 08:00
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2.5}>
            {[
              { label: 'approved', n: approved, color: GREEN },
              { label: 'pending',  n: pending,  color: '#9ca3af' },
              ...(rejected > 0 ? [{ label: 'rejected', n: rejected, color: '#ef4444' }] : []),
            ].map(({ label, n, color }) => (
              <Typography key={label} sx={{ fontSize: 12, color: '#6b7280' }}>
                <strong style={{ color }}>{n}</strong> {label}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
